import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spin, Tooltip, message } from 'antd';
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { getBillingClaimDetail, getBillingEraPdfLink, getBillingEraRemittance } from 'api/insuranceBilling';
import type {
    ClaimDetailVo,
    EraPdfLinkVo,
    EraRemitClaimVo,
    EraRemitTotalsVo,
    EraRemittanceVo,
    TBillingApiScope,
} from 'types/insuranceBilling';
import { formatDateMDY, formatMoney } from './statusConfig';
import s from './s.module.less';

export const NO_ERA_PDF_MSG = 'No PDF is available for this remittance yet.';
// hasPdf=false 时置灰的 Download 按钮悬停提示
export const NO_ERA_PDF_TIP = 'PDF not available yet. Check back in a few days.';

// 取预签名短链（900 秒过期，每次点击现取，不缓存）并触发浏览器下载；
// 返回是否成功（false = 无 PDF / 接口不通，真实接口无 PDF 时 400）
export const downloadEraPdf = async (paymentId: number): Promise<boolean> => {
    let link: EraPdfLinkVo | null = null;
    try {
        const res: any = await getBillingEraPdfLink(paymentId);
        if (!res?.error) link = res?.data?.data ?? null;
    } catch (e) {
        link = null;
    }
    if (!link?.url) return false;
    const a = document.createElement('a');
    a.href = link.url;
    a.download = `remittance-835-${paymentId}.pdf`;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
};

type Props = {
    // ERA 行的 paymentId（非 id）；claim 列表进来时是 claim 的 remittancePaymentId
    paymentId: number;
    // 从 claim 列表进来：只展示这张 claim 的条目。remittance 接口按支票展开，没有按 claim 查的接口，
    // 暂由前端过滤；后端出 claim 级接口后替换
    claimId?: number;
    // claim 模式下拉 claim 详情（判定是否早先提交的回款单）用的端点 scope
    apiScope?: TBillingApiScope;
    onClose: () => void;
};

// 支票里属于这张 claim 的条目。同一张支票里同一 claim 可能出现两次（payer 冲正 + 重付），
// 所以 filter 不 find；claimId 匹配不上时按 ERA 行自己的 paymentId 兜底
const pickClaimBlocks = (claims: EraRemitClaimVo[], claimId: number, paymentId: number) => {
    const byClaim = claims.filter((c) => c.claimId === claimId);
    if (byClaim.length) return byClaim;
    return claims.filter((c) => c.paymentId === paymentId);
};

// 过滤后的合计按所选 claim 重算（接口 totals 是整张支票的）；全部缺失时保持 undefined 显示 —
const sumTotals = (claims: EraRemitClaimVo[]): EraRemitTotalsVo => {
    const sum = (k: keyof EraRemitTotalsVo) => {
        const vals = claims.map((c) => c.totals?.[k]).filter((v): v is number => typeof v === 'number');
        return vals.length ? vals.reduce((a, b) => a + b, 0) : undefined;
    };
    return {
        billed: sum('billed'),
        allowed: sum('allowed'),
        adjustments: sum('adjustments'),
        patientResp: sum('patientResp'),
        paid: sum('paid'),
    };
};

// 状态 pill（原型 remitPill）：status 为上游推算值（规则见 EraRemittanceVo.status），与 AskChart 835 页
// （super-agent-web-app MR 219）同口径；未知值按 Paid 兜底
const remitPill = (status?: string) => {
    if (status === 'PARTIALLY_PAID') return { cls: s.remPillPartial, label: 'Partial payment', ok: true };
    if (status === 'PATIENT_RESPONSIBLE') return { cls: s.remPillPatient, label: 'Patient responsibility', ok: true };
    if (status === 'DENIED') return { cls: s.remPillDenied, label: 'Denied', ok: false };
    return { cls: s.remPillPaid, label: 'Paid', ok: true, paid: true };
};

const adjGroupCls = (group?: string) => {
    if (group === 'CO') return s.remCodeCo;
    if (group === 'PR') return s.remCodePr;
    return s.remCodeOa;
};

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div>
        <div className={s.remFieldLabel}>{label}</div>
        <div className={s.remFieldValue}>{value || '—'}</div>
    </div>
);

// 一张支票支付的单笔 claim：Claim & patient 字段行 + service lines 表（含 adjustment 子行）
const ClaimBlock = ({ claim, index, total }: { claim: EraRemitClaimVo; index: number; total: number }) => (
    <div>
        <div className={s.remSec}>Claim &amp; patient{total > 1 ? ` (${index + 1}/${total})` : ''}</div>
        <div className={`${s.remFieldRow} ${s.remFieldRowWrap}`}>
            <Field label="Patient" value={claim.patient?.name} />
            <Field label="Member ID" value={claim.patient?.memberId} />
            <Field label="Account #" value={claim.patient?.accountNumber} />
            <Field label="Claim control #" value={claim.claimControl} />
        </div>
        <div className={s.remSec}>Service lines</div>
        <div className={s.remTableWrap}>
            <table className={s.remTable}>
                <thead>
                    <tr>
                        <th>DOS</th>
                        <th>CPT</th>
                        <th className={s.remNum}>UNITS</th>
                        <th className={s.remNum}>BILLED</th>
                        <th className={s.remNum}>ALLOWED</th>
                        <th className={s.remNum}>PAID</th>
                    </tr>
                </thead>
                <tbody>
                    {(claim.serviceLines || []).map((sl, i) => (
                        // service line 无稳定 id，索引 key
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={i}>
                            <tr>
                                <td>{formatDateMDY(sl.dos)}</td>
                                <td className={s.monoCell}>
                                    {sl.cpt}
                                    {sl.modifier ? ` ${sl.modifier}` : ''}
                                </td>
                                <td className={s.remNum}>{sl.units ?? '—'}</td>
                                <td className={s.remNum}>{formatMoney(sl.billed)}</td>
                                <td className={s.remNum}>{formatMoney(sl.allowed)}</td>
                                <td className={`${s.remNum} ${s.remSlPaid}`}>{formatMoney(sl.paid)}</td>
                            </tr>
                            {!!sl.adjustments?.length && (
                                <tr className={s.remSlAdjRow}>
                                    <td colSpan={6}>
                                        <div className={s.remAdjList}>
                                            {sl.adjustments.map((a) => (
                                                <div className={s.remAdjItem} key={`${a.group}-${a.code}`}>
                                                    <span className={`${s.remCode} ${adjGroupCls(a.group)}`}>
                                                        {a.group}-{a.code}
                                                    </span>
                                                    <span className={s.remAdjDesc}>{a.description}</span>
                                                    <span className={s.remAdjAmt}>{formatMoney(a.amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// 835 汇款单全屏文档页（原型 openRemittance / eraDocHtml），portal 到 body 覆盖 dashboard
const RemittanceDoc = ({ paymentId, claimId, apiScope = 'practice', onClose }: Props) => {
    const [data, setData] = useState<EraRemittanceVo | null>(null);
    // 这张回款单属于这条 claim 更早一次的提交（claim 重新提交后还在审核时，指向的仍是旧回款单）。
    // remittance 接口不带该标记，从 claim 详情 payment.forEarlierSubmission 取
    const [forEarlier, setForEarlier] = useState(false);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        // 跨支票的 835 用 pdfPaymentId 定位 PDF
        const ok = await downloadEraPdf(data?.pdfPaymentId ?? paymentId);
        if (!ok) message.error(NO_ERA_PDF_MSG);
        setDownloading(false);
    };

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            // 接口不通 / 无明细（404）都落到 null → "No remittance available" 空态
            const loadRemittance = async (): Promise<EraRemittanceVo | null> => {
                try {
                    const res: any = await getBillingEraRemittance(paymentId);
                    return !res?.error ? (res?.data?.data ?? null) : null;
                } catch (e) {
                    return null;
                }
            };
            // claim 详情只用来判定 earlier 标签：失败 / 非 claim 模式都按「不是早先的」处理，不挡 835 展示
            const loadForEarlier = async (): Promise<boolean> => {
                if (claimId == null) return false;
                try {
                    const res: any = await getBillingClaimDetail(claimId, apiScope);
                    const detail: ClaimDetailVo | null = !res?.error ? (res?.data?.data ?? null) : null;
                    const payment = detail?.payment;
                    // 只有 claim 的 payment 就是这张回款单时才认它的标记，避免把标签挂到另一张单上
                    return !!payment && payment.paymentId === paymentId && payment.forEarlierSubmission;
                } catch (e) {
                    return false;
                }
            };
            const [vo, earlier] = await Promise.all([loadRemittance(), loadForEarlier()]);
            if (cancelled) return;
            setData(vo);
            setForEarlier(earlier);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [paymentId, claimId, apiScope]);

    const claimMode = claimId != null;
    const allClaims = data?.claims || [];
    const claims = claimMode ? pickClaimBlocks(allClaims, claimId, paymentId) : allClaims;
    // claim 模式下支票里找不到这张 claim → 按无明细处理，不把整张支票（其他患者）摊出来
    const empty = data == null || (claimMode && !claims.length);

    const renderDoc = (r: EraRemittanceVo) => {
        // claim 模式：只有一条时用这条 claim 自己的状态，冲正 + 重付两条时退回支票状态
        const pill = remitPill(claimMode && claims.length === 1 ? claims[0].status : r.status);
        const totals = claimMode ? sumTotals(claims) : r.totals;
        return (
            <div className={s.remDoc}>
                <div className={s.remHead}>
                    <div className={s.remHeadLeft}>
                        <FileTextOutlined className={s.remHeadIcon} />
                        <h1>Remittance Advice (835)</h1>
                    </div>
                    <div className={s.remHeadPills}>
                        {/* 早先那次提交的回款单：一眼看出不是当前这次（claim 可能已重新提交、还在审核） */}
                        {claimMode && forEarlier && (
                            <span className={`${s.remPill} ${s.remPillEarlier}`}>From an earlier submission</span>
                        )}
                        <span className={`${s.remPill} ${pill.cls}`}>
                            {pill.ok ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            {pill.label}
                        </span>
                        {/* Paid 但患者还欠钱（copay 等）：那是诊所要向患者收的，不是 payer 少付（QA t104335） */}
                        {pill.paid && (totals?.patientResp ?? 0) > 0 && (
                            <span className={s.remPatientOwes}>Patient owes {formatMoney(totals?.patientResp)}</span>
                        )}
                    </div>
                </div>
                <div className={s.remBand}>
                    <div className={s.remBandCap}>Electronic Remittance Advice</div>
                    <div className={s.remBandSub}>Transaction set 835 &middot; Payer to provider</div>
                    <div className={s.remFieldRow}>
                        <Field label="Payment date" value={formatDateMDY(r.payment?.date)} />
                        <Field
                            label={r.payment?.method === 'EFT' ? 'EFT trace #' : 'Check #'}
                            value={r.payment?.number}
                        />
                        {/* claim 模式：一张支票可能付了多条 claim，payment.amount 是整张支票的，
                            改用过滤后这条 claim 的实付（与底部 Paid 同口径） */}
                        <Field
                            label="Payment amount"
                            value={formatMoney(claimMode ? totals?.paid : r.payment?.amount)}
                        />
                    </div>
                </div>
                <div className={s.remCols}>
                    <div>
                        <div className={s.remSec}>Payer</div>
                        <div className={s.remName}>{r.payer?.name || '—'}</div>
                        <div className={s.remMuted}>{r.payer?.address}</div>
                        <div className={s.remMuted}>Payer ID: {r.payer?.payerId || '—'}</div>
                    </div>
                    <div>
                        <div className={s.remSec}>Payee</div>
                        <div className={s.remName}>{r.payee?.name || '—'}</div>
                        <div className={s.remMuted}>NPI: {r.payee?.npi || '—'}</div>
                        <div className={s.remMuted}>Tax ID: {r.payee?.taxId || '—'}</div>
                    </div>
                </div>
                {claims.map((c, i) => (
                    <ClaimBlock claim={c} index={i} total={claims.length} key={c.claimId ?? c.claimControl ?? i} />
                ))}
                <div className={s.remLegend}>
                    CO = Contractual obligation (provider write-off) &middot; PR = Patient responsibility &middot; OA =
                    Other adjustment
                </div>
                <div className={s.remTotals}>
                    <div className={s.remTot}>
                        <span>Billed</span>
                        <span>{formatMoney(totals?.billed)}</span>
                    </div>
                    <div className={s.remTot}>
                        <span>Allowed</span>
                        <span>{formatMoney(totals?.allowed)}</span>
                    </div>
                    <div className={s.remTot}>
                        <span>Adjustments</span>
                        <span>{formatMoney(totals?.adjustments)}</span>
                    </div>
                    <div className={s.remTot}>
                        <span>Patient resp.</span>
                        <span>{formatMoney(totals?.patientResp)}</span>
                    </div>
                    <div className={`${s.remTot} ${s.remTotPaid}`}>
                        <span>Paid</span>
                        <span>{formatMoney(totals?.paid)}</span>
                    </div>
                </div>
            </div>
        );
    };

    const body = (
        <div className={s.remScreen}>
            <div className={s.remTop}>
                <Button type="link" icon={<ArrowLeftOutlined />} onClick={onClose}>
                    Back to claims
                </Button>
                {/* 后端标记无 PDF：直接置灰，不发请求，悬停说明原因 */}
                <Tooltip title={data?.hasPdf === false ? NO_ERA_PDF_TIP : undefined}>
                    <Button
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={data?.hasPdf === false}
                        onClick={handleDownload}
                    >
                        Download 835
                    </Button>
                </Tooltip>
            </div>
            {loading ? (
                <div className={s.remLoading}>
                    <Spin />
                </div>
            ) : (
                empty && (
                    <div className={s.remEmpty}>
                        <FileTextOutlined className={s.remEmptyIcon} />
                        <div className={s.remEmptyTitle}>No remittance available</div>
                    </div>
                )
            )}
            {!loading && !empty && data != null && renderDoc(data)}
        </div>
    );

    return createPortal(body, document.body);
};

export default RemittanceDoc;
